import React, { useEffect, useRef } from 'react';
import { Pageable, SortConfiguration } from '../../../../types/rest/paginationTypes';
import { RecordingMessage } from '../../../../types/rest/assetCatalogTypes';
import { BlazeTableColDef, BlazeTableOptions, SelectAllType } from '../../../../components/blazeTable/BlazeTable.types';
import { useDispatch } from 'react-redux';
import BlazeTable from '../../../../components/blazeTable/BlazeTable';
import {
  changeRecordingMessagesPageSize,
  sortRecordingMessages,
} from '../../../../store/recordings/recordingMessages/recordingMessagesReducer';
import i18n from '../../../../i18n';
import BlazeTableActions from '../../../../components/blazeTable/BlazeTableActions';
import BzmIcon from '../../../../components/icons/BzmIcon';
import { Button } from 'react-bootstrap';
import {
  fetchRecordingMessagesThunk,
  getRecordingMessageById,
  updateActiveRecordingMessagesPage,
} from '../../../../store/recordings/recordingMessages/recordingMessagesActions';
import _ from 'lodash';
import { getErrorMessage } from '../../../../utils/miscUtil';
import { showToastNotification } from '../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../store/ui/toasts/toastsTypes';
import { messageTypes } from '../../../../types/rest/dslTypes';
import { getDateWithFormat } from '../../../../utils/dateUtil';

interface RecordingMessagesTableProps {
  recordingId: number;
  recordingMessages: RecordingMessage[];
  isCreatingNewMessage: boolean;
  expandedRecordingMessageId: number | null;
  onSelectionChange: (selectedItems: Array<any>) => void;
  onDelete: (message: RecordingMessage) => void;
  onOpenDetail: (message: RecordingMessage) => void;
  selectedItems: number[];
  isLoading: boolean;
  recordingMessagePageable: Pageable;
  recordingMessageSort: SortConfiguration;
  totalMessages: number;
  selectAllType: null | SelectAllType;
  isCompact?: boolean;
}

const RecordingsMessagesTable = (props: RecordingMessagesTableProps) => {
  const dispatch = useDispatch();
  const tableRef = useRef<BlazeTable>(null);

  const isNewMessage = (message: RecordingMessage) => {
    return message.id === -1;
  };

  useEffect(() => {
    if (tableRef.current && props.isCreatingNewMessage) {
      tableRef.current.expandRowHandler(-1);
    }
  }, [props.isCreatingNewMessage]);

  const colsDefinition: BlazeTableColDef[] = [
    {
      header: 'Index',
      cellComponent: (message: RecordingMessage) => {
        if (isNewMessage(message)) {
          return '-';
        }
        return <span>{message.index}</span>;
      },
      isSortable: false,
      accessor: 'index',
      width: 60,
    },
    {
      header: 'Id',
      cellComponent: (message: RecordingMessage) => {
        if (isNewMessage(message)) {
          return '-';
        }
        return <span>{message.id}</span>;
      },
      isSortable: false,
      accessor: 'id',
      width: 60,
    },
    {
      header: 'Name',
      cellComponent: (message: RecordingMessage) => {
        if (isNewMessage(message)) {
          return '-';
        }
        return (
          <div className="d-flex align-items-center">
            <div style={{ marginTop: '6px' }}>
              <BzmIcon
                title={i18n.t('recordings:table.messages.message')}
                variant={'icon-bzm-email'}
                className={'icon-xs mr-2'}
              />
            </div>
            <span>{message.name}</span>
          </div>
        );
      },
      accessor: 'name',
    },
    {
      header: 'Correlation Id',
      cellComponent: (message: RecordingMessage) => {
        if (isNewMessage(message)) {
          return '-';
        }
        return (
          <span>
            {message.correlationId !== null && message.correlationId !== undefined && message.correlationId !== ''
              ? message.correlationId
              : '-'}
          </span>
        );
      },
      isSortable: false,
      accessor: 'correlationId',
    },
    {
      header: 'Type',
      cellComponent: (message: RecordingMessage) => {
        if (isNewMessage(message)) {
          return '-';
        }
        return <span>{messageTypes[message.messageType]}</span>;
      },
      isSortable: false,
      accessor: 'type',
    },
    {
      header: 'Destination',
      cellComponent: (message: RecordingMessage) => {
        if (isNewMessage(message)) {
          return '-';
        }
        return <span>{message.destination}</span>;
      },
      isSortable: false,
      accessor: 'destination',
    },
    {
      header: 'Recorded At',
      cellComponent: (message: RecordingMessage) => {
        const itemDate =
          message.recordedAt === null || message.recordedAt === undefined || message.recordedAt === 0
            ? '-'
            : new Date(message.recordedAt);
        if (isNewMessage(message)) {
          return '-';
        }
        return <span>{itemDate === '-' ? itemDate : getDateWithFormat(itemDate)}</span>;
      },
      isSortable: false,
      accessor: 'recordedAt',
    },
    {
      header: 'Actions',
      headerComponent: <div className={'text-center'}>Actions</div>,
      width: 130,
      cellClass: 'text-right',
      accessor: 'value',
      isSortable: false,
      cellComponent: message => {
        return (
          <div className={'text-left'}>
            <BlazeTableActions>
              <Button className={'btn-link-dark btn-no-hover'}>
                <BzmIcon
                  variant={'icon-bzm-edit'}
                  className={'icon-xs'}
                  title={i18n.t('recordings:table.messages.edit')}
                  onClick={() => openMessageEditor(message.id)}
                />
              </Button>
              <Button className={'btn-link-dark btn-no-hover'}>
                <BzmIcon
                  title={i18n.t('recordings:table.messages.delete')}
                  variant={'icon-bzm-delete'}
                  className={'icon-xs'}
                  onClick={() => props.onDelete(message)}
                />
              </Button>
            </BlazeTableActions>
          </div>
        );
      },
    },
  ];

  const openMessageEditor = async (messageId: number) => {
    try {
      const message = await getRecordingMessageById(props.recordingId, messageId);
      props.onOpenDetail(message);
    } catch (e) {
      const error = getErrorMessage(e);
      dispatch(showToastNotification(ToastType.Danger, 'Something went wrong', error));
    }
  };

  const prepareData = (): RecordingMessage[] => {
    return _.clone(props.recordingMessages);
    /*    const recordingMessages: RecordingMessage[] = _.clone(props.recordingMessages);
    if (props.isCreatingNewRecordingMessage) {
      recordingMessages.unshift(getEmptyRecordingMessage());
    }
    return recordingMessages;*/
  };

  const tableOptions: BlazeTableOptions = {
    showRowExpander: false,
    columnDef: colsDefinition,
    pageable: true,
    selectable: true,
    sortable: true,
    selectAllDropdown: true,
    paginationOverride: {
      activePage: props.recordingMessagePageable.skip,
      pageSize: props.recordingMessagePageable.limit,
      total: props.totalMessages,
    },
    sortingOverride: props.recordingMessageSort,
    selectAllType: props.selectAllType,
    tableName: 'messagesList',
    loadingText: 'Loading Messages...',
    noResultsText: i18n.t('recordings:table.messages.noResults'),
  };

  return (
    <BlazeTable
      selectedItems={props.selectedItems}
      onSelectionChange={props.onSelectionChange}
      options={tableOptions}
      data={prepareData()}
      isLoading={props.isLoading}
      ref={tableRef}
      onSort={sortConfig => {
        dispatch(sortRecordingMessages(sortConfig));
        dispatch(fetchRecordingMessagesThunk(props.recordingId, true));
      }}
      showPageSizeDropdown={!props.isCompact}
      onPageChange={pageNumber => dispatch(updateActiveRecordingMessagesPage(props.recordingId, pageNumber))}
      onPageSizeChange={pageSize => dispatch(changeRecordingMessagesPageSize(pageSize))}
    />
  );
};

export default RecordingsMessagesTable;
